import type { CSSProperties, ReactNode } from "react";
import clsx from "clsx";
import type { components } from "@cntrl/api-client";
import type { SmartLinkStatsRead } from "../../app/hooks/useRelease.js";
import { DSPS } from "./dsp.js";
import { lifetimeClicks } from "./releaseFormat.js";
import styles from "./SmartLinkStats.module.scss";

type DspLinkDto = components["schemas"]["DspLinkDto"];

// The smart-link STAT PRESENTATION, shared by the command centre's step 04 and the hub's expandable
// smart-link row. The reference's rule for that row is "copy, edit & read stats WITHOUT opening it" — the
// figures and the per-DSP bars are the reason the row exists — so both surfaces must read the same numbers
// from the same component rather than drifting apart.
//
// The LEAK CONTRACT is the load-bearing part and travels with the component: a metric with no defined value
// renders "—", NEVER a fabricated 0, and a read error degrades the same way (an errored fetch must not be
// allowed to assert "0 clicks"). A genuinely loaded 0 is honest and renders "0".

export function StatTile({ label, value }: { label: string; value: number | string | null }) {
  // Grouped, because the hero's `DarkStatTile` groups: measured side by side, the same lifetime-clicks
  // figure read "6,832" on the banner and "6832" one card over.
  const shown = value == null ? "—" : typeof value === "number" ? value.toLocaleString("en-US") : value;
  return (
    <div className={styles.tile}>
      <span className={styles.tileLabel}>{label}</span>
      <span className={styles.tileValue}>{shown}</span>
    </div>
  );
}

export interface SmartLinkStatTilesProps {
  stats?: SmartLinkStatsRead;
  isError: boolean;
  /**
   * The hub row's middle figure is `Clicks · lifetime` (the reference's own headline pairs lifetime clicks
   * with the conversion), summed from `lifetimeClicksByDsp`. The section keeps the 30-day `DSP clicks`.
   *
   * `Visits · Lifetime` and `Visits · MTD` — the reference's other two headline figures — are deliberately
   * OMITTED: `SmartLinkStatsRead` serves only a rolling 30-day visit window, and relabelling 30d as MTD
   * would be a lie about the window (G-3 / R-1).
   */
  lifetime?: boolean;
  /** Steps the figure down for the hub row (three tiles inside a workspace card, not a page statement). */
  compact?: boolean;
}

export function SmartLinkStatTiles({ stats, isError, lifetime, compact }: SmartLinkStatTilesProps) {
  const unavailable = isError || !stats;
  const pct = stats?.visitToClickPct;
  const clicks = lifetime ? lifetimeClicks(stats, isError) : unavailable ? null : stats!.clicks30d ?? null;
  return (
    <div className={clsx(styles.tiles, compact && styles.compact)}>
      <StatTile label="Visits · 30d" value={unavailable ? null : stats!.visits30d ?? null} />
      <StatTile label={lifetime ? "Clicks · lifetime" : "DSP clicks"} value={clicks} />
      <StatTile label="Visit → click" value={unavailable || pct == null ? null : `${formatPct(pct)}%`} />
    </div>
  );
}

export interface DspClicksChartProps {
  /**
   * The release's configured DSP buttons. BOTH surfaces pass them (the hub row reads the release's composed
   * detail lazily, exactly as it reads its stats), so both build the same row set: a configured DSP with no
   * recorded clicks is a `0` row on the section AND on the hub, never "none yet" on one and "0" on the other.
   * Omitted only where the caller genuinely has no list yet — and then the empty branch says so rather than
   * asserting there have been no clicks.
   */
  dspLinks?: DspLinkDto[];
  stats?: SmartLinkStatsRead;
  isError: boolean;
  /** Rendered in place of the generic "add streaming links" line when there are no rows — for a caller that knows
   *  why the release has no links and can say how to fix it. Shown whether or not the stats read succeeded. */
  emptyAction?: ReactNode;
}

/**
 * The lifetime per-DSP click bars, in the FIXED `DSPS` registry order on every surface (the reference's own
 * desktop/mobile sheets disagree — desktop uses a fixed order, mobile sorts by count; the export flags the
 * disagreement and we take the fixed one).
 *
 * Two denominators, deliberately, exactly as the reference computes them: the BAR width is each DSP's share
 * of the MAX row, so the tallest bar always fills the track and small differences stay readable; the VALUE is
 * the raw lifetime count.
 */
export function DspClicksChart({ dspLinks = [], stats, isError, emptyAction }: DspClicksChartProps) {
  const unavailable = isError || !stats;

  const byDsp = new Map<string, number>(
    (stats?.lifetimeClicksByDsp ?? []).map((c) => [c.dsp ?? "", c.clicks ?? 0] as [string, number]),
  );
  byDsp.delete("");

  // Show the union of the release's current DSP buttons (branded/ordered) and any DSP that has historical clicks,
  // in the DSPS registry order — a current button with a loaded 0 shows an empty bar; a since-removed DSP with clicks
  // still surfaces its lifetime total. When unavailable, byDsp is empty, so the rows are the configured buttons
  // alone and every value reads "—" (below) rather than a fabricated 0.
  // Only the registry's buttons chart: a key outside it — `deezer`, retired as a button (ART-219) but still legal
  // in stored link rows and historical clicks — is dropped rather than drawn under its raw key.
  const linkKeys = dspLinks.filter((l) => l.url && l.dsp).map((l) => l.dsp as string);
  const keys = new Set<string>([...linkKeys, ...byDsp.keys()]);
  const rows = DSPS.filter((d) => keys.has(d.key)).map((d) => ({ def: d, clicks: byDsp.get(d.key) ?? 0 }));

  const max = rows.reduce((m, r) => Math.max(m, r.clicks), 0);

  return (
    <div className={styles.chart}>
      <p className={styles.chartLabel}>DSP clicks · lifetime</p>
      {rows.length === 0 ? (
        // The empty branch is reached ONLY when the release has no resolved DSP links (a link, auto-filled or
        // manual, renders a 0-click bar rather than falling here) AND no historical clicks — so the honest
        // empty copy names the missing links, not the missing clicks: the reason there is nothing to show is
        // there is nothing to click yet (ART-219). Unavailable stays its own message; the leak contract keeps
        // it out of the "loaded, genuinely empty" copy so we never assert "nothing yet" over a read we could
        // not complete.
        // `emptyAction` replaces the generic line when the caller knows WHY there are no links and how to fix it
        // (Smart Link: no tracks to look links up from → "Go to Assets", ART-219). It wins over "unavailable" too:
        // it is a statement about the release's LINKS, which the click-stats read has no bearing on.
        emptyAction ? (
          emptyAction
        ) : (
          <p className={styles.chartEmpty}>
            {unavailable
              ? "Per-DSP clicks aren't available right now."
              : "Add streaming links to start tracking DSP clicks."}
          </p>
        )
      ) : (
        <ul className={styles.barList}>
          {rows.map(({ def, clicks }) => (
            <li key={def.key} className={styles.barRow}>
              <span className={styles.barName}>{def.label}</span>
              <span className={styles.barTrack}>
                <span
                  className={styles.barFill}
                  style={
                    {
                      width: `${!unavailable && max > 0 ? (clicks / max) * 100 : 0}%`,
                      background: `var(${def.colorVar})`,
                    } as CSSProperties
                  }
                />
              </span>
              <span className={styles.barVal}>{unavailable ? "—" : clicks}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Trim a trailing ".0" so a whole-percent conversion reads "80%", not "80.0%". */
function formatPct(pct: number): string {
  return Number.isInteger(pct) ? String(pct) : pct.toFixed(1);
}
